import moment from 'moment';
import { sortNames } from 'logic/sortNames';
import { BillingService, ContractType, ContractServices, UtcTime, Contract, OrgId } from 'shared/models';
import { BuiltInOrgIds } from 'shared/orgs';

// The strings here are not translated because only MAD
// users will be using the UI where these strings appear.
// i.e. they are only needed by English-speaking users.

export const BillingStrings = {
    UnbilledServices: 'Unbilled Services:',
    OverilledServices: 'Overbilled Services:',
    Columns: {
        Services: 'Services',
        Buildings: 'Buildings',
        Price: 'Price',
        Type: 'Type',
        Expiry: 'Expiry',
    },
    ADD_DIALOG: {
        NEW_TITLE: 'Add a New Contract',
    },
    BILLING: "Billing",
    BILLING_CONTRACT: 'Billing Contract',
    CURRENCY: 'Currency',
    EXPIRED: 'Expired',
    EXPIRED_ALREADY: 'This contract is already expired but still has buildings assigned to it',
    EXPIRING_SOON: 'This contract is expiring soon and has buildings assigned to it',
    EXPIRY_DATE: 'Expiry Date',
    NOT_APPLICABLE: 'n/a',
    PAYMENT_DATE: 'Payment Date',
    PER_MONTH: ' / month',
    PLUS_ONE_YEAR: '+1 Year',
    PRICE: 'Price',
    RENEW_CONTRACT: 'Renew Contract',
    REPLACE_EXISTING: 'Replace Existing',
    SERVICES: 'Services',
    TYPE: 'Type',
};

export const ContractTypeName = {
    [ContractType.Monthly]: 'Monthly',
    [ContractType.FixedTerm]: 'Fixed Term',
};

export const BillingServiceName = {
    [BillingService.Data]: 'Data',
    [BillingService.Insights]: 'Insights',
    [BillingService.PhoneAnswering]: 'Phone Answering',
    [BillingService.RemoteAccess]: 'Remote Access',
    [BillingService.VOIP]: 'VOIP',
};

export const makeContractServicesString = (services: ContractServices = {}) =>
    Object.keys(services)
        .map(service => BillingServiceName[service as BillingService])
        .sort(sortNames)
        .join(', ');

export const getEligibleServicesByOrg = (orgId: OrgId) =>
    orgId === BuiltInOrgIds.TsaroOrgId
        ? [ BillingService.Insights ]
        : [ BillingService.Data, BillingService.PhoneAnswering, BillingService.VOIP, BillingService.RemoteAccess ];

const formatDate = (date: UtcTime | null) => date ? moment(date).format('MMM DD, YYYY') : '';

export type ExpiryMessage = {
    text: string; // Just the preamble (e.g. "Expires on")
    date?: string; // Date in string form
    isExpired: boolean;
    message: string; // The full message
};

export function getExpiryMessageHtml(expiryDate: UtcTime | null, onlyDate: boolean) {
    const { text, date, isExpired } = getExpiryMessage(expiryDate, onlyDate);
    return (
        <span className="expiry-message">
            { `${text} ` }
            { date &&
                <b title={ expiryDate ? formatDate(expiryDate) : undefined }>
                    { date }
                </b>
            }
            { isExpired &&
                <i
                    className="red warning circle icon contract-expired-icon"
                    title="Expired"
                ></i>
            }
        </span>
    );
}

export function getExpiryStatusHtml(contract: Contract) {
    // Warn the user when a fixed-term contract is expired / expiring soon but still has buildings assigned to it
    const isWarningVisible =
        contract.type === ContractType.FixedTerm &&
        moment(contract.expiryDate).diff(moment(), 'months') < 1 &&
        !!contract.assignments;

    return (
        <span className={ isWarningVisible ? 'expiring-soon' : undefined }>
            { isContractExpired(contract)
                ? BillingStrings.EXPIRED
                : contract.type === ContractType.FixedTerm
                    ? getDateFromNow(contract.expiryDate)
                    : BillingStrings.NOT_APPLICABLE
            }
            { isWarningVisible && (
                isContractExpired(contract)
                    ? <i
                        className="red warning circle icon"
                        title={ BillingStrings.EXPIRED_ALREADY }
                    />
                    : <i
                        className="yellow exclamation triangle icon"
                        title={ BillingStrings.EXPIRING_SOON }
                    />
            ) }
        </span>
    );
}

export function getExpiryDateString(contract: Contract) {
    return (
        contract.type === ContractType.FixedTerm
            ? formatDate(contract.expiryDate!)
            : ''
    );
}

function getDateFromNow(expiryDate: UtcTime | null) {
    if (!expiryDate) {
        return '';
    }

    const now = moment();
    const expiry = moment(expiryDate);
    const diffInDays = expiry.diff(now, 'days');
    const diffInWeeks = expiry.diff(now, 'weeks');
    const diffInMonths = expiry.diff(now, 'months');

    const fromNow = diffInDays < 14
        ? `${diffInDays} days`
        : diffInWeeks < 8
            ? `${diffInWeeks} weeks`
            : `${diffInMonths} months`;

    return fromNow;
}

export const isContractExpired = ({ expiryDate }: Contract): boolean => !!expiryDate && expiryDate < Date.now();

export function getExpiryMessage(expiryDate: UtcTime | null, onlyDate: boolean): ExpiryMessage {
    if (!expiryDate) {
        return {
            text: onlyDate ? 'n/a' : 'Monthly',
            message: onlyDate ? 'n/a' : 'Monthly',
            isExpired: false,
        };
    }

    const isExpired = expiryDate < Date.now();

    return onlyDate
        ? {
            text: '',
            date: isExpired ? formatDate(expiryDate) : getDateFromNow(expiryDate),
            message: isExpired ? `Expired on ${formatDate(expiryDate)}` : `Expires in ${getDateFromNow(expiryDate)}`,
            isExpired,
        } : {
            text: isExpired ? 'Expired' : 'Expires ',
            date: isExpired ? undefined : formatDate(expiryDate),
            message: isExpired ? `Expired` : `Expires in ${getDateFromNow(expiryDate)}`,
            isExpired,
        };
}
